import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@spotted-zebra-uk/ui-components';
import Icon from '@/components/atoms/Icon';
import { AVATAR_OPTIONS, AvatarOption } from '../AvatarSelection/avatarOptions';
import { Avatar } from '../../../generated/graphql';
import styles from './StepChooseInterviewer.module.scss';

const SPOT_ONLY: AvatarOption[] = AVATAR_OPTIONS.filter(o => o.id === Avatar.Spot);

interface StepChooseInterviewerProps {
  isAvatarEnabled: boolean;
  selectedAvatar: AvatarOption | null;
  onAvatarSelect: (avatar: AvatarOption) => void;
  onContinue: () => void;
}

export function StepChooseInterviewer({
  isAvatarEnabled,
  selectedAvatar,
  onAvatarSelect,
  onContinue,
}: StepChooseInterviewerProps) {
  const { t } = useTranslation();
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});

  const options = isAvatarEnabled ? AVATAR_OPTIONS : SPOT_ONLY;

  function handleOptionClick(option: AvatarOption) {
    // Pause previous video
    if (selectedAvatar && selectedAvatar.id !== option.id) {
      const prev = videoRefs.current[selectedAvatar.id];
      if (prev) {
        prev.pause();
        prev.currentTime = 0;
      }
    }

    onAvatarSelect(option);
    setVideoPlaying(false);

    const video = videoRefs.current[option.id];
    if (video) {
      video.currentTime = 0;
      video.play();
    }
  }

  const setVideoRef = useCallback(
    (id: string) => (el: HTMLVideoElement | null) => {
      videoRefs.current[id] = el;
    },
    []
  );

  const isSelected = (option: AvatarOption) => option.id === selectedAvatar?.id;

  return (
    <div className={styles.stepChoose}>
      <div className={styles.content}>
        <h1 className={styles.title}>{t('aiInterview.chooseInterviewer')}</h1>

        <div className={styles.optionsList}>
          {options.map(option => (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={isSelected(option)}
              className={`${styles.optionTile} ${isSelected(option) ? styles.optionTileSelected : ''}`}
              onClick={() => handleOptionClick(option)}
            >
              <div className={styles.optionAvatar}>
                {option.videoUrl && (
                  <video
                    ref={setVideoRef(option.id)}
                    playsInline
                    className={styles.optionVideo}
                    onPlaying={() => setVideoPlaying(true)}
                    onEnded={() => setVideoPlaying(false)}
                  >
                    <source src={option.videoUrl} type="video/mp4" />
                  </video>
                )}
                <img
                  src={option.thumbnailUrl}
                  alt={option.name}
                  className={`${styles.optionImage} ${
                    isSelected(option) && videoPlaying ? styles.optionImageFaded : ''
                  }`}
                />
              </div>
              <span className={styles.optionName}>{option.name}</span>
              {isSelected(option) && (
                <div className={styles.optionCheck} aria-hidden="true" />
              )}
            </button>
          ))}
        </div>
      </div>

      <Button
        onClick={onContinue}
        disabled={!selectedAvatar}
        fullWidth
        rightIcon={<Icon icon="right_arrow" />}
      >
        {t('aiInterview.tryPracticeFirst')}
      </Button>
    </div>
  );
}
